import React, { useState } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// The unified problem list: each flag/reviewReason as bold label + sentence.
const ProblemList = ({ problems }) =>
	problems.map((p) => (
		<Box key={p.code} sx={{ marginBottom: 1.5 }}>
			<Typography variant="body2" sx={{ fontWeight: 600 }}>{p.label}</Typography>
			<Typography variant="body2" sx={{ color: 'text.secondary' }}>{p.description}</Typography>
		</Box>
	));

// Acknowledge actions: the fix path (Cancel) + the consequential Continue-anyway (red).
// Once acknowledged, the buttons give way to a plain confirmation line.
const ReviewActions = ({ copy, acknowledged, onConfirm, onCancel }) =>
	acknowledged ? (
		<Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
			{copy.acknowledge.acknowledged}
		</Typography>
	) : (
		<Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
			<Button variant="outlined" size="small" onClick={onCancel}>{copy.acknowledge.cancel}</Button>
			<Button variant="contained" color="error" size="small" onClick={onConfirm}>
				{copy.acknowledge.proceed}
			</Button>
		</Box>
	);

// The `expanded` drawer: the answer to the "Verify document" step. Collapsed by default (worker sees
// only the "We couldn't verify this document" headline); expanding reveals flags, consequence, actions.
const ExpandedFeedback = ({ problems, copy, acknowledged, onConfirm, onCancel }) => {
	const [open, setOpen] = useState(false);

	return (
		<Box role="alert" sx={{ textAlign: 'left' }}>
			<ButtonBase
				onClick={acknowledged ? undefined : () => setOpen((v) => !v)}
				disabled={acknowledged}
				aria-expanded={open && !acknowledged}
				sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', textAlign: 'left', py: 0.5, borderRadius: 1 }}
			>
				<WarningAmberIcon sx={{ color: 'error.main', flexShrink: 0 }} />
				<Typography sx={{ fontWeight: 600, color: 'error.main', flex: 1 }}>
					{copy.acknowledge.reviewTitle || copy.acknowledge.title}
				</Typography>
				{!acknowledged && (open
					? <ExpandLessIcon sx={{ color: 'error.main' }} />
					: <ExpandMoreIcon sx={{ color: 'error.main' }} />)}
			</ButtonBase>

			{acknowledged ? (
				<Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
					{copy.acknowledge.acknowledged}
				</Typography>
			) : (
				<Collapse in={open} timeout="auto" unmountOnExit>
					<Box sx={{ pt: 1.5 }}>
						<ProblemList problems={problems} />
						<Typography variant="body2" sx={{ mt: 2, mb: 3, color: 'text.secondary' }}>
							{copy.acknowledge.explainer}
						</Typography>
						<ReviewActions copy={copy} acknowledged={false} onConfirm={onConfirm} onCancel={onCancel} />
					</Box>
				</Collapse>
			)}
		</Box>
	);
};

// The legacy `collapsible` presentation: tap-to-reveal "Why is this document flagged?" ListItem, used
// by the multi-document ScanningPage which owns the expanded/onToggle state.
const CollapsibleFeedback = ({ problems, copy, acknowledged, expanded, onToggle, onConfirm, onCancel }) => (
	<>
		<Divider />
		<ListItem
			button
			onClick={onToggle}
			sx={(theme) => ({
				backgroundColor: alpha(theme.palette.error.main, 0.08),
				'&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.12) },
			})}
		>
			<ListItemIcon sx={{ minWidth: 40 }}>
				<WarningAmberIcon sx={{ color: 'error.main' }} />
			</ListItemIcon>
			<ListItemText
				primary={copy.acknowledge.title}
				primaryTypographyProps={{ variant: 'body2', fontWeight: 500, sx: { color: 'error.main' } }}
			/>
			<ListItemIcon sx={{ minWidth: 'auto' }}>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</ListItemIcon>
		</ListItem>

		<Collapse in={expanded} timeout="auto" unmountOnExit>
			<Box sx={(theme) => ({ padding: 2, backgroundColor: alpha(theme.palette.error.main, 0.04), textAlign: 'left' })}>
				<ProblemList problems={problems} />
				<Typography variant="body2" sx={{ marginTop: 2, marginBottom: 3, color: 'text.secondary' }}>{copy.acknowledge.explainer}</Typography>
				<ReviewActions copy={copy} acknowledged={acknowledged} onConfirm={onConfirm} onCancel={onCancel} />
			</Box>
		</Collapse>
	</>
);

// Presentational feedback + acknowledge unit for an in-review document. `expanded` = guided single-doc
// capture (reasons shown in the open); `collapsible` = legacy multi-doc list (tap-to-reveal).
const DocumentReviewFeedback = ({ variant = 'collapsible', ...props }) => {
	if (!props.problems?.length) return null;
	return variant === 'expanded' ? <ExpandedFeedback {...props} /> : <CollapsibleFeedback {...props} />;
};

export default DocumentReviewFeedback;
